import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Target, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Calendar,
  Zap,
  BarChart3,
  Mail,
  Link,
  Globe,
  CheckCircle2,
  Rocket
} from 'lucide-react';

interface PlanMarketingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Section = 'summary' | 'roadmap' | 'tactics' | 'budget' | 'kpis' | 'quickstart';

export default function PlanMarketingModal({ isOpen, onClose }: PlanMarketingModalProps) {
  const [activeSection, setActiveSection] = useState<Section>('summary');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
                  <Rocket className="w-8 h-8" />
                  Plan Marketing & Stratégie 2025
                </h2>
                <p className="text-white/90 text-lg">Objectif: 20-25 Clients | Revenue: 200k$ | ROI: 10x 🔥</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="grid grid-cols-6 gap-2 mt-6">
              {[
                { id: 'summary', icon: Target, label: 'Summary' },
                { id: 'roadmap', icon: Calendar, label: 'Roadmap' },
                { id: 'tactics', icon: Zap, label: 'Tactiques' },
                { id: 'budget', icon: DollarSign, label: 'Budget' },
                { id: 'kpis', icon: BarChart3, label: 'KPIs' },
                { id: 'quickstart', icon: Rocket, label: 'Quick Start' }
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id as Section)}
                  className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                    activeSection === id
                      ? 'bg-white text-purple-600 shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              {activeSection === 'summary' && <SummarySection key="summary" />}
              {activeSection === 'roadmap' && <RoadmapSection key="roadmap" />}
              {activeSection === 'tactics' && <TacticsSection key="tactics" />}
              {activeSection === 'budget' && <BudgetSection key="budget" />}
              {activeSection === 'kpis' && <KPIsSection key="kpis" />}
              {activeSection === 'quickstart' && <QuickStartSection key="quickstart" />}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================
// SECTION: SUMMARY
// ============================================
function SummarySection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Hero Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6 text-center">
          <Target className="w-12 h-12 mx-auto mb-2 opacity-80" />
          <p className="text-4xl font-bold mb-1">20-25</p>
          <p className="text-sm opacity-90">Clients Signés</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 text-center">
          <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-80" />
          <p className="text-4xl font-bold mb-1">200k$</p>
          <p className="text-sm opacity-90">Revenue Annuel</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-80" />
          <p className="text-4xl font-bold mb-1">10x</p>
          <p className="text-sm opacity-90">ROAS (Return On Ad Spend)</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-6 text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-80" />
          <p className="text-4xl font-bold mb-1">800-1000$</p>
          <p className="text-sm opacity-90">CAC Target</p>
        </div>
      </div>

      {/* Stratégie Unique */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-6">
        <h3 className="text-2xl font-bold text-blue-900 mb-3">🎯 Positionnement Unique (USP)</h3>
        <p className="text-lg text-gray-800 leading-relaxed">
          "Le seul diagnostic TI qui combine <strong>l'intelligence artificielle GPT-4</strong> et 
          <strong> l'expertise humaine certifiée</strong> pour livrer en <strong>2-12 semaines</strong> ce que 
          les Big 4 font en 6 mois pour <strong>5x le prix</strong>."
        </p>
      </div>

      {/* Segments Cibles */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">👥 Segments Cibles (Priorités)</h3>
        
        <div className="space-y-4">
          {/* Segment A */}
          <div className="bg-white border-2 border-red-300 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-bold text-red-700">🔥 Segment A - PRIORITY 1</h4>
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-bold">60% efforts</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-bold text-gray-900 mb-2">Profil:</p>
                <ul className="space-y-1 text-gray-700">
                  <li>• PME 50-200 employés</li>
                  <li>• Services professionnels, Santé</li>
                  <li>• Budget: 8-12k$</li>
                  <li>• Niveau: Professionnel</li>
                </ul>
              </div>
              <div>
                <p className="font-bold text-gray-900 mb-2">Pain Points:</p>
                <ul className="space-y-1 text-gray-700">
                  <li>• Conformité (Loi 25, PIPEDA)</li>
                  <li>• Croissance rapide</li>
                  <li>• Cyber-risque élevé</li>
                  <li>• Besoin gouvernance TI</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Segment B */}
          <div className="bg-white border-2 border-orange-300 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-bold text-orange-700">⚡ Segment B - PRIORITY 2</h4>
              <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-bold">25% efforts</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-bold text-gray-900 mb-2">Profil:</p>
                <ul className="space-y-1 text-gray-700">
                  <li>• TPE 5-50 employés</li>
                  <li>• Commerce, Services locaux</li>
                  <li>• Budget: 3-5k$</li>
                  <li>• Niveau: Essentiel</li>
                </ul>
              </div>
              <div>
                <p className="font-bold text-gray-900 mb-2">Pain Points:</p>
                <ul className="space-y-1 text-gray-700">
                  <li>• Budget limité</li>
                  <li>• Premier audit/découverte</li>
                  <li>• Simplicité recherchée</li>
                  <li>• Quick wins attendus</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Segment C */}
          <div className="bg-white border-2 border-purple-300 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-bold text-purple-700">💎 Segment C - PRIORITY 3</h4>
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-bold">15% efforts</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-bold text-gray-900 mb-2">Profil:</p>
                <ul className="space-y-1 text-gray-700">
                  <li>• Grande org. 200+ employés</li>
                  <li>• Manufacturier, Finance, Gouv.</li>
                  <li>• Budget: 18-40k$</li>
                  <li>• Niveau: Expert</li>
                </ul>
              </div>
              <div>
                <p className="font-bold text-gray-900 mb-2">Pain Points:</p>
                <ul className="space-y-1 text-gray-700">
                  <li>• Complexité infrastructure</li>
                  <li>• Conformité stricte (ISO, SOC2)</li>
                  <li>• Audits réglementaires</li>
                  <li>• Décision collégiale</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sources d'Acquisition */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6">
        <h3 className="text-xl font-bold text-green-900 mb-4">📊 Mix Canaux (Target Annuel)</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-300">
              <span className="text-sm font-bold text-gray-900">💎 Partenariats CPA</span>
              <span className="text-lg font-bold text-green-600">6 clients (30%)</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-300">
              <span className="text-sm font-bold text-gray-900">💼 LinkedIn Organic</span>
              <span className="text-lg font-bold text-blue-600">5 clients (25%)</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-purple-300">
              <span className="text-sm font-bold text-gray-900">🤝 Référence Clients</span>
              <span className="text-lg font-bold text-purple-600">4 clients (20%)</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-indigo-300">
              <span className="text-sm font-bold text-gray-900">📢 LinkedIn Ads</span>
              <span className="text-lg font-bold text-indigo-600">2-3 clients (12%)</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-300">
              <span className="text-sm font-bold text-gray-900">🔍 Google Ads</span>
              <span className="text-lg font-bold text-amber-600">1-2 clients (8%)</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-300">
              <span className="text-sm font-bold text-gray-900">📧 Cold Email + Autres</span>
              <span className="text-lg font-bold text-gray-600">1 client (5%)</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// SECTION: ROADMAP
// ============================================
function RoadmapSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h3 className="text-2xl font-bold text-gray-900">📅 Roadmap 12 Mois (Par Trimestre)</h3>

      {/* Q1 */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-bold text-blue-900">Q1 (Jan-Mars): FONDATIONS & LANCEMENT 🏗️</h4>
          <div className="text-right">
            <p className="text-2xl font-bold text-blue-600">3 clients</p>
            <p className="text-sm text-blue-700">20k-30k$</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <p className="font-bold text-gray-900 mb-2">🔧 Setup (S1-2)</p>
            <ul className="space-y-1 text-gray-700">
              <li>• CRM pipeline (HubSpot)</li>
              <li>• Email marketing setup</li>
              <li>• Calendly pour démos</li>
              <li>• Deck présentation ventes</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <p className="font-bold text-gray-900 mb-2">📝 Contenu (S3-4)</p>
            <ul className="space-y-1 text-gray-700">
              <li>• 3 case studies détaillées</li>
              <li>• 1-pager PDF par segment</li>
              <li>• Vidéo explicative 2 min</li>
              <li>• Checklist lead magnet</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <p className="font-bold text-gray-900 mb-2">📞 Prospection (S5-8)</p>
            <ul className="space-y-1 text-gray-700">
              <li>• 50 LinkedIn/semaine (200 total)</li>
              <li>• 100 emails/semaine (400 total)</li>
              <li>• 20 appels/jour (400 total)</li>
              <li>• 2 événements networking/mois</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <p className="font-bold text-gray-900 mb-2">📈 Inbound (S9-12)</p>
            <ul className="space-y-1 text-gray-700">
              <li>• SEO: 10 pages optimisées</li>
              <li>• Blog: 8 articles (2/semaine)</li>
              <li>• LinkedIn Ads: 2k$/mois</li>
              <li>• Google Ads: 1.5k$/mois</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Q2 */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-bold text-purple-900">Q2 (Avr-Juin): SCALING & AUTOMATION 📈</h4>
          <div className="text-right">
            <p className="text-2xl font-bold text-purple-600">5 clients</p>
            <p className="text-sm text-purple-700">40k-50k$</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <p className="font-bold text-gray-900 mb-2">🤖 Automatisation</p>
            <ul className="space-y-1 text-gray-700">
              <li>• Séquences email auto</li>
              <li>• Chatbot qualification</li>
              <li>• LinkedIn automation</li>
              <li>• Webinaires mensuels</li>
              <li>• Quiz interactif</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <p className="font-bold text-gray-900 mb-2">🤝 Partenariats</p>
            <ul className="space-y-1 text-gray-700">
              <li>• 10 CPA (15% commission)</li>
              <li>• 5 Avocats TI/IP</li>
              <li>• 8 MSP</li>
              <li>• 3 Courtiers assurances</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <p className="font-bold text-gray-900 mb-2">📚 Content Marketing</p>
            <ul className="space-y-1 text-gray-700">
              <li>• 12 articles blog</li>
              <li>• 4 guides téléchargeables</li>
              <li>• Newsletter mensuelle</li>
              <li>• 6 posts LinkedIn/semaine</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Q3 */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-bold text-amber-900">Q3 (Juil-Sept): DOMINATION & REFERRALS 👑</h4>
          <div className="text-right">
            <p className="text-2xl font-bold text-amber-600">6 clients</p>
            <p className="text-sm text-amber-700">60k-65k$</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4 border border-amber-200">
            <p className="font-bold text-gray-900 mb-2">🎁 Programme Référence</p>
            <ul className="space-y-1 text-gray-700">
              <li>• "BZ Ambassadeur"</li>
              <li>• 500$ crédit ou 15% cash</li>
              <li>• Kit référence</li>
              <li>• Événement clients</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-amber-200">
            <p className="font-bold text-gray-900 mb-2">📖 Authority Building</p>
            <ul className="space-y-1 text-gray-700">
              <li>• Livre blanc industrie</li>
              <li>• 3 mentions médias</li>
              <li>• 2 conférences</li>
              <li>• 3 podcasts invité</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-amber-200">
            <p className="font-bold text-gray-900 mb-2">🎯 ABM (Dream Accounts)</p>
            <ul className="space-y-1 text-gray-700">
              <li>• 50 dream accounts</li>
              <li>• Campagnes personnalisées</li>
              <li>• Video Loom custom</li>
              <li>• 5 contrats Expert (90k$+)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Q4 */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-bold text-green-900">Q4 (Oct-Déc): FINISH STRONG 💪</h4>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600">6 clients</p>
            <p className="text-sm text-green-700">80k-85k$</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <p className="font-bold text-gray-900 mb-2">💸 Budget Flush</p>
            <ul className="space-y-1 text-gray-700">
              <li>• Campagne "Budget 2025"</li>
              <li>• 10% rabais avant 31 déc</li>
              <li>• Package fin d'année</li>
              <li>• Urgency messaging</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <p className="font-bold text-gray-900 mb-2">🔄 Retention & Upsell</p>
            <ul className="space-y-1 text-gray-700">
              <li>• Services post-diagnostic</li>
              <li>• Programme "Diagnostic Annuel"</li>
              <li>• Packages "Suivi Trimestriel"</li>
              <li>• Revenue additionnel 30-50%</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <p className="font-bold text-gray-900 mb-2">📊 Planning 2026</p>
            <ul className="space-y-1 text-gray-700">
              <li>• Analyser metrics 2025</li>
              <li>• Top 3 canaux acquisition</li>
              <li>• Scaling 2026 (200 clients)</li>
              <li>• Team expansion</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// SECTION: TACTIQUES
// ============================================
function TacticsSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h3 className="text-2xl font-bold text-gray-900">🎯 Tactiques D'Acquisition Détaillées</h3>

      {/* LinkedIn Outreach */}
      <div className="bg-white border-2 border-blue-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link className="w-8 h-8 text-blue-600" />
            <h4 className="text-xl font-bold text-gray-900">LinkedIn Outreach</h4>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">ROI</p>
            <p className="text-2xl font-bold text-blue-600">10/10 🔥</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="font-bold text-gray-900 mb-2">📊 Volume</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• 50-100 connexions/semaine (automatisé)</li>
              <li>• Taux acceptation: 40-50%</li>
              <li>• Taux réponse: 20-30%</li>
              <li>• Taux conversion démo: 30-40%</li>
            </ul>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="font-bold text-gray-900 mb-2">🛠️ Outils</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• LinkedIn Sales Navigator (80$/mois)</li>
              <li>• PhantomBuster (60$/mois)</li>
              <li>• Profil optimisé + bannière</li>
              <li>• Templates messages testés</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg p-4 border-2 border-blue-300">
          <p className="font-bold text-gray-900 mb-2">✨ Résultat Attendu:</p>
          <p className="text-lg font-bold text-blue-700">24-96 clients/an via LinkedIn 🚀</p>
        </div>
      </div>

      {/* Cold Email */}
      <div className="bg-white border-2 border-purple-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Mail className="w-8 h-8 text-purple-600" />
            <h4 className="text-xl font-bold text-gray-900">Cold Email Automation</h4>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">ROI</p>
            <p className="text-2xl font-bold text-purple-600">8/10</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <p className="font-bold text-gray-900 mb-2">📊 Volume</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• 100-200 emails/semaine (automatisé)</li>
              <li>• Taux ouverture: 40-50%</li>
              <li>• Taux réponse: 5-10%</li>
              <li>• Taux conversion démo: 20-30%</li>
            </ul>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <p className="font-bold text-gray-900 mb-2">🛠️ Outils</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Apollo.io / Hunter.io (99$/mois)</li>
              <li>• Lemlist / Mailshake (59$/mois)</li>
              <li>• Warmup Inbox (15$/mois)</li>
              <li>• Séquence 4 emails testée</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-100 to-purple-50 rounded-lg p-4 border-2 border-purple-300">
          <p className="font-bold text-gray-900 mb-2">✨ Résultat Attendu:</p>
          <p className="text-lg font-bold text-purple-700">12-48 clients/an via Email ⚡</p>
        </div>
      </div>

      {/* Partenariats CPA */}
      <div className="bg-white border-2 border-green-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-green-600" />
            <h4 className="text-xl font-bold text-gray-900">Partenariats Comptables/CPA</h4>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">ROI</p>
            <p className="text-2xl font-bold text-green-600">9/10 💎</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="font-bold text-gray-900 mb-2">🎯 Stratégie</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• 15-20 cabinets CPA actifs</li>
              <li>• Commission 15% par référence</li>
              <li>• 2-5 références/cabinet/an</li>
              <li>• Taux closing: 50% (trust élevé)</li>
            </ul>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="font-bold text-gray-900 mb-2">💡 Pourquoi Ça Marche?</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Trust transféré du CPA</li>
              <li>• Timing parfait (fin d'année fiscale)</li>
              <li>• Win-Win (service additionnel)</li>
              <li>• Pas d'effort pour le CPA</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-100 to-emerald-50 rounded-lg p-4 border-2 border-green-300">
          <p className="font-bold text-gray-900 mb-2">✨ Résultat Attendu:</p>
          <p className="text-lg font-bold text-green-700">15-50 clients/an via CPA 🎰 JACKPOT!</p>
        </div>
      </div>

      {/* Webinaires */}
      <div className="bg-white border-2 border-amber-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Globe className="w-8 h-8 text-amber-600" />
            <h4 className="text-xl font-bold text-gray-900">Webinaires Mensuels</h4>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">ROI</p>
            <p className="text-2xl font-bold text-amber-600">7/10</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="font-bold text-gray-900 mb-2">📊 Format</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Thème: "3 Erreurs Fatales Cybersécurité"</li>
              <li>• 30-50 participants/webinaire</li>
              <li>• Show-up rate: 50-60%</li>
              <li>• Démos bookées: 20-30%</li>
            </ul>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="font-bold text-gray-900 mb-2">📈 Promotion</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• LinkedIn Ads: 500$ budget</li>
              <li>• Email liste: 500-1000 contacts</li>
              <li>• Posts organiques (2 semaines avant)</li>
              <li>• Co-promo avec partenaires CPA</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-amber-100 to-orange-50 rounded-lg p-4 border-2 border-amber-300">
          <p className="font-bold text-gray-900 mb-2">✨ Résultat Attendu:</p>
          <p className="text-lg font-bold text-amber-700">12-36 clients/an via Webinaires 📚</p>
        </div>
      </div>

      {/* LinkedIn/Google Ads */}
      <div className="bg-white border-2 border-red-400 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-red-600" />
            <h4 className="text-xl font-bold text-gray-900">LinkedIn & Google Ads</h4>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">ROI</p>
            <p className="text-2xl font-bold text-red-600">6/10</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="font-bold text-gray-900 mb-2">💼 LinkedIn Ads</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Budget: 2 000$/mois (24k$/an)</li>
              <li>• Lead gen: checklist gratuit</li>
              <li>• 100 leads/mois, 20 démos/mois</li>
              <li>• 6-7 clients/mois</li>
            </ul>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="font-bold text-gray-900 mb-2">🔍 Google Ads</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Budget: 1 500$/mois (18k$/an)</li>
              <li>• Intent-based (bottom funnel)</li>
              <li>• 20-30 leads/mois, 8-12 démos/mois</li>
              <li>• 3-4 clients/mois</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-red-100 to-pink-50 rounded-lg p-4 border-2 border-red-300">
          <p className="font-bold text-gray-900 mb-2">✨ Résultat Attendu:</p>
          <p className="text-lg font-bold text-red-700">108-132 clients/an via Ads 💸</p>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// SECTION: BUDGET
// ============================================
function BudgetSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h3 className="text-2xl font-bold text-gray-900">💰 Budget Marketing Annuel</h3>

      {/* Total Budget */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl p-8 text-center">
        <p className="text-sm opacity-90 mb-2">BUDGET MARKETING TOTAL</p>
        <p className="text-6xl font-bold mb-2">20 000$</p>
        <p className="text-lg opacity-90">(budget lean & efficace!)</p>
      </div>

      {/* Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Outils & Software */}
        <div className="bg-white border-2 border-blue-400 rounded-xl p-6">
          <h4 className="text-xl font-bold text-blue-900 mb-4">🛠️ Outils & Software</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">CRM (HubSpot Starter)</span>
              <span className="font-bold text-gray-900">540$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">LinkedIn Sales Navigator</span>
              <span className="font-bold text-gray-900">960$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Apollo.io (email scraping)</span>
              <span className="font-bold text-gray-900">1 188$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Lemlist (cold email)</span>
              <span className="font-bold text-gray-900">708$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">PhantomBuster (automation)</span>
              <span className="font-bold text-gray-900">720$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Email Marketing (ActiveCampaign)</span>
              <span className="font-bold text-gray-900">2 400$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Chatbot (Drift/Intercom)</span>
              <span className="font-bold text-gray-900">1 200$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Content writer (blog)</span>
              <span className="font-bold text-gray-900">12 000$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Design & Video</span>
              <span className="font-bold text-gray-900">5 000$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Autres outils & Misc</span>
              <span className="font-bold text-gray-900">7 284$/an</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t-2 border-blue-400">
              <span className="font-bold text-gray-900">TOTAL OUTILS:</span>
              <span className="font-bold text-blue-600 text-lg">7 000$/an</span>
            </div>
          </div>
        </div>

        {/* Ads & Marketing */}
        <div className="bg-white border-2 border-purple-400 rounded-xl p-6">
          <h4 className="text-xl font-bold text-purple-900 mb-4">📢 Ads & Marketing</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">LinkedIn Ads</span>
              <span className="font-bold text-gray-900">24 000$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Google Ads</span>
              <span className="font-bold text-gray-900">18 000$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Events & Networking</span>
              <span className="font-bold text-gray-900">8 000$/an</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-700">Partnerships Setup</span>
              <span className="font-bold text-gray-900">11 000$/an</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t-2 border-purple-400">
              <span className="font-bold text-gray-900">TOTAL ADS & MARKETING:</span>
              <span className="font-bold text-purple-600 text-lg">13 000$/an</span>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-purple-50 border border-purple-300 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">💡 Note:</p>
            <p className="text-xs text-gray-700">
              Les commissions CPA (15%) et le programme référence sont payés sur revenue, 
              pas upfront. Budget réel ajustable selon performance.
            </p>
          </div>
        </div>
      </div>

      {/* ROI Calculation */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6">
        <h4 className="text-xl font-bold text-green-900 mb-4">📊 ROI & Performance</h4>
        
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-lg p-4 border border-green-300 text-center">
            <p className="text-sm text-gray-600 mb-1">Budget Marketing</p>
            <p className="text-3xl font-bold text-gray-900">20 000$</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-green-300 text-center">
            <p className="text-sm text-gray-600 mb-1">Revenue Estimé</p>
            <p className="text-3xl font-bold text-green-600">200k$</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-green-300 text-center">
            <p className="text-sm text-gray-600 mb-1">ROAS</p>
            <p className="text-3xl font-bold text-green-600">10x</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-green-300">
            <p className="text-sm text-gray-600 mb-2">CAC Target (Coût Acquisition Client)</p>
            <p className="text-2xl font-bold text-gray-900">800$ - 1 000$</p>
            <p className="text-xs text-gray-500 mt-1">Baisse au fil du temps grâce automation</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-green-300">
            <p className="text-sm text-gray-600 mb-2">LTV Target (Lifetime Value)</p>
            <p className="text-2xl font-bold text-gray-900">{`> 20 000$`}</p>
            <p className="text-xs text-gray-500 mt-1">{`Ratio LTV:CAC > 10:1 (excellent)`}</p>
          </div>
        </div>
      </div>

      {/* SDR Optionnel */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl p-6">
        <h4 className="text-xl font-bold text-amber-900 mb-3">👤 SDR (Sales Dev Rep) - Optionnel</h4>
        <p className="text-sm text-gray-700 mb-4">
          À embaucher après Q2 si le volume de leads devient trop élevé pour gérer manuellement.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-amber-300">
            <p className="text-sm text-gray-600 mb-1">Base Salaire</p>
            <p className="text-2xl font-bold text-gray-900">45 000$/an</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-amber-300">
            <p className="text-sm text-gray-600 mb-1">Commission</p>
            <p className="text-2xl font-bold text-gray-900">15 000$/an</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-amber-300">
            <p className="text-sm text-gray-600 mb-1">Total</p>
            <p className="text-2xl font-bold text-amber-600">60 000$/an</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// SECTION: KPIs
// ============================================
function KPIsSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h3 className="text-2xl font-bold text-gray-900">📊 Métriques & KPIs</h3>

      {/* Dashboard Mensuel */}
      <div className="bg-white border-2 border-purple-400 rounded-xl p-6">
        <h4 className="text-xl font-bold text-purple-900 mb-4">📈 Dashboard Mensuel (Tracking)</h4>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-purple-100">
              <tr>
                <th className="px-4 py-3 text-left font-bold text-purple-900">Métrique</th>
                <th className="px-4 py-3 text-center font-bold text-purple-900">Target/Mois</th>
                <th className="px-4 py-3 text-center font-bold text-purple-900">Q1</th>
                <th className="px-4 py-3 text-center font-bold text-purple-900">Q2</th>
                <th className="px-4 py-3 text-center font-bold text-purple-900">Q3</th>
                <th className="px-4 py-3 text-center font-bold text-purple-900">Q4</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-gray-900">Leads</td>
                <td className="px-4 py-3 text-center text-gray-700">150</td>
                <td className="px-4 py-3 text-center text-blue-600 font-bold">450</td>
                <td className="px-4 py-3 text-center text-purple-600 font-bold">600</td>
                <td className="px-4 py-3 text-center text-amber-600 font-bold">750</td>
                <td className="px-4 py-3 text-center text-green-600 font-bold">750</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50">
                <td className="px-4 py-3 font-bold text-gray-900">Démos</td>
                <td className="px-4 py-3 text-center text-gray-700">30</td>
                <td className="px-4 py-3 text-center text-blue-600 font-bold">90</td>
                <td className="px-4 py-3 text-center text-purple-600 font-bold">120</td>
                <td className="px-4 py-3 text-center text-amber-600 font-bold">150</td>
                <td className="px-4 py-3 text-center text-green-600 font-bold">150</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-gray-900">Clients Signés</td>
                <td className="px-4 py-3 text-center text-gray-700">8-10</td>
                <td className="px-4 py-3 text-center text-blue-600 font-bold">15</td>
                <td className="px-4 py-3 text-center text-purple-600 font-bold">25</td>
                <td className="px-4 py-3 text-center text-amber-600 font-bold">30</td>
                <td className="px-4 py-3 text-center text-green-600 font-bold">30</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50">
                <td className="px-4 py-3 font-bold text-gray-900">Revenue</td>
                <td className="px-4 py-3 text-center text-gray-700">50k-80k</td>
                <td className="px-4 py-3 text-center text-blue-600 font-bold">120k</td>
                <td className="px-4 py-3 text-center text-purple-600 font-bold">200k</td>
                <td className="px-4 py-3 text-center text-amber-600 font-bold">240k</td>
                <td className="px-4 py-3 text-center text-green-600 font-bold">240k</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-gray-900">CAC</td>
                <td className="px-4 py-3 text-center text-gray-700">{`< 1 500$`}</td>
                <td className="px-4 py-3 text-center text-blue-600 font-bold">1 500$</td>
                <td className="px-4 py-3 text-center text-purple-600 font-bold">1 200$</td>
                <td className="px-4 py-3 text-center text-amber-600 font-bold">1 000$</td>
                <td className="px-4 py-3 text-center text-green-600 font-bold">900$</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Sources Attribution */}
      <div className="bg-white border-2 border-green-400 rounded-xl p-6">
        <h4 className="text-xl font-bold text-green-900 mb-4">🎯 Sources Attribution (Target Annuel)</h4>
        
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border-2 border-green-400">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900">💎 Partenariats CPA</span>
              <span className="text-2xl font-bold text-green-600">30 clients</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <p className="text-gray-600">% Total</p>
                <p className="font-bold text-gray-900">30%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">CAC</p>
                <p className="font-bold text-gray-900">500$</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Budget</p>
                <p className="font-bold text-gray-900">15k$</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-100 to-cyan-100 rounded-lg p-4 border-2 border-blue-400">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900">💼 LinkedIn Organic</span>
              <span className="text-2xl font-bold text-blue-600">25 clients</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <p className="text-gray-600">% Total</p>
                <p className="font-bold text-gray-900">25%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">CAC</p>
                <p className="font-bold text-gray-900">800$</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Budget</p>
                <p className="font-bold text-gray-900">20k$</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-4 border-2 border-purple-400">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900">🤝 Référence Clients</span>
              <span className="text-2xl font-bold text-purple-600">20 clients</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <p className="text-gray-600">% Total</p>
                <p className="font-bold text-gray-900">20%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">CAC</p>
                <p className="font-bold text-gray-900">250$</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Budget</p>
                <p className="font-bold text-gray-900">5k$</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-100 to-blue-100 rounded-lg p-4 border border-indigo-300">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900">📢 LinkedIn Ads</span>
              <span className="text-2xl font-bold text-indigo-600">12 clients</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <p className="text-gray-600">% Total</p>
                <p className="font-bold text-gray-900">12%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">CAC</p>
                <p className="font-bold text-gray-900">2 000$</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Budget</p>
                <p className="font-bold text-gray-900">24k$</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-4 border border-amber-300">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900">🔍 Google Ads</span>
              <span className="text-2xl font-bold text-amber-600">8 clients</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <p className="text-gray-600">% Total</p>
                <p className="font-bold text-gray-900">8%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">CAC</p>
                <p className="font-bold text-gray-900">2 250$</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Budget</p>
                <p className="font-bold text-gray-900">18k$</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg p-4 border border-gray-300">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900">📧 Cold Email + Autres</span>
              <span className="text-2xl font-bold text-gray-600">5 clients</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <p className="text-gray-600">% Total</p>
                <p className="font-bold text-gray-900">5%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">CAC</p>
                <p className="font-bold text-gray-900">1 500$</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Budget</p>
                <p className="font-bold text-gray-900">11k$</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Metrics */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-400 rounded-xl p-6">
        <h4 className="text-xl font-bold text-purple-900 mb-4">✅ Success Metrics (Milestones)</h4>
        
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-5 border-l-4 border-blue-500">
            <p className="font-bold text-gray-900 mb-2">🎯 Semaine 1-4</p>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>200 prospects contactés</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>10 démos bookées</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>2-3 clients signés</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>1 partenariat CPA initié</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 border-l-4 border-purple-500">
            <p className="font-bold text-gray-900 mb-2">🎯 Mois 1-3 (Q1)</p>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>600 prospects contactés</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>45 démos réalisées</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>15 clients signés (15% objectif)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>3 partenariats CPA actifs</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 border-l-4 border-amber-500">
            <p className="font-bold text-gray-900 mb-2">🎯 Mois 4-6 (Q2)</p>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>1 000 prospects contactés</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>75 démos réalisées</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>25 clients signés (25% objectif)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>8 partenariats actifs</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 border-l-4 border-green-500">
            <p className="font-bold text-gray-900 mb-2">🎯 Fin d'Année (Mois 7-12)</p>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>100 clients total signés</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Revenue 700k-1.2M$</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>{`CAC final < 1 000$`}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>40% nouveaux clients via référence</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// SECTION: QUICK START
// ============================================
function QuickStartSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h3 className="text-2xl font-bold text-gray-900">🚀 Quick Start (Prochaines 48H)</h3>

      {/* Hero CTA */}
      <div className="bg-gradient-to-r from-red-500 via-pink-500 to-purple-500 text-white rounded-xl p-8 text-center">
        <h4 className="text-3xl font-bold mb-3">⚡ ACTION IMMÉDIATE!</h4>
        <p className="text-xl mb-4">Ces 8 actions vont lancer la machine de guerre en 48h chrono!</p>
        <div className="flex items-center justify-center gap-2">
          <Rocket className="w-8 h-8" />
          <span className="text-2xl font-bold">LET'S FUCKING GO! 🔥</span>
        </div>
      </div>

      {/* To-Do Liste */}
      <div className="space-y-3">
        <div className="bg-white border-2 border-blue-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              1
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ Setup CRM</h5>
              <p className="text-sm text-gray-600 mb-3">
                HubSpot gratuit ou Pipedrive (trial) - Créer pipeline: Lead → Contacted → Demo → Proposal → Closed
              </p>
              <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-800">
                <strong>Temps:</strong> 30 min | <strong>Priorité:</strong> CRITIQUE
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-purple-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              2
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ Créer Calendly</h5>
              <p className="text-sm text-gray-600 mb-3">
                Lien pour démos 15 min - Configurer disponibilités, rappels auto, intégration CRM
              </p>
              <div className="bg-purple-50 rounded-lg p-3 text-xs text-purple-800">
                <strong>Temps:</strong> 15 min | <strong>Priorité:</strong> HAUTE
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-green-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              3
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ LinkedIn Profile Optimization</h5>
              <p className="text-sm text-gray-600 mb-3">
                Photo pro, Headline: "Expert Diagnostic TI | 200+ audits | CISSP", Bannière avec offre BZ
              </p>
              <div className="bg-green-50 rounded-lg p-3 text-xs text-green-800">
                <strong>Temps:</strong> 45 min | <strong>Priorité:</strong> HAUTE
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-amber-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              4
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ Première Liste 50 Prospects</h5>
              <p className="text-sm text-gray-600 mb-3">
                LinkedIn Sales Navigator - Filtres: Directeur Général, 50-200 employés, Québec, Services professionnels
              </p>
              <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
                <strong>Temps:</strong> 1h | <strong>Priorité:</strong> CRITIQUE
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-red-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              5
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ Template Email Outreach</h5>
              <p className="text-sm text-gray-600 mb-3">
                Adapter le script cold email du plan - Personnaliser par segment (A, B, C)
              </p>
              <div className="bg-red-50 rounded-lg p-3 text-xs text-red-800">
                <strong>Temps:</strong> 30 min | <strong>Priorité:</strong> HAUTE
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-indigo-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-indigo-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              6
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ Contacter 10 Prospects TODAY</h5>
              <p className="text-sm text-gray-600 mb-3">
                5 LinkedIn (message connexion) + 5 Email (cold outreach) - PRENDRE ACTION MAINTENANT!
              </p>
              <div className="bg-indigo-50 rounded-lg p-3 text-xs text-indigo-800">
                <strong>Temps:</strong> 1h | <strong>Priorité:</strong> CRITIQUE ⚡
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-pink-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              7
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ Identifier 5 CPA Potentiels</h5>
              <p className="text-sm text-gray-600 mb-3">
                Recherche LinkedIn - Cabinets comptables 5-20 professionnels au Québec
              </p>
              <div className="bg-pink-50 rounded-lg p-3 text-xs text-pink-800">
                <strong>Temps:</strong> 30 min | <strong>Priorité:</strong> MOYENNE
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-teal-400 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
              8
            </div>
            <div className="flex-1">
              <h5 className="text-lg font-bold text-gray-900 mb-2">✅ Bloquer 2h/Jour Prospection</h5>
              <p className="text-sm text-gray-600 mb-3">
                Dans calendrier (9h-11h ou 14h-16h) - NON-NÉGOCIABLE! Traiter comme un meeting client.
              </p>
              <div className="bg-teal-50 rounded-lg p-3 text-xs text-teal-800">
                <strong>Temps:</strong> 5 min | <strong>Priorité:</strong> CRITIQUE
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mindset */}
      <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-400 rounded-xl p-6">
        <h4 className="text-xl font-bold text-amber-900 mb-4">🧠 Mindset Pour Réussir</h4>
        
        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
              1
            </div>
            <div>
              <p className="font-bold text-gray-900">{`Discipline > Motivation`}</p>
              <p className="text-gray-700">2h prospection/jour, TOUS LES JOURS. Pas d'excuses.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
              2
            </div>
            <div>
              <p className="font-bold text-gray-900">Measure Everything</p>
              <p className="text-gray-700">Track religieusement tes metrics dans le CRM. Données = décisions.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
              3
            </div>
            <div>
              <p className="font-bold text-gray-900">Iterate Fast</p>
              <p className="text-gray-700">Teste, apprends, ajuste chaque semaine. Pas chaque mois.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
              4
            </div>
            <div>
              <p className="font-bold text-gray-900">Partnerships = Cheat Code</p>
              <p className="text-gray-700">Focus massif sur CPA. C'est le canal le plus ROI-efficient.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
              5
            </div>
            <div>
              <p className="font-bold text-gray-900">Content = Credibility</p>
              <p className="text-gray-700">Publie, publie, publie. LinkedIn est ta vitrine.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
              6
            </div>
            <div>
              <p className="font-bold text-gray-900">Follow-Up = $$$</p>
              <p className="text-gray-700">80% des ventes se font après 5+ touchpoints. Persévère.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
              7
            </div>
            <div>
              <p className="font-bold text-gray-900">Be Relentless</p>
              <p className="text-gray-700">"No" = "Not yet". Ne jamais abandonner.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final Motivation */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white rounded-xl p-8 text-center">
        <h4 className="text-3xl font-bold mb-3">🔥 TU VAS ÉCRASER! 🔥</h4>
        <div className="text-xl space-y-2">
          <p>20-25 clients = 200k$ revenue</p>
          <p>20-25 clients = 20-25 entreprises plus sécurisées</p>
          <p>20-25 clients = Fondation solide pour l'expansion</p>
        </div>
        <div className="mt-6 text-3xl font-bold">
          LET'S FUCKING GO! 🚀💪🔥
        </div>
      </div>
    </motion.div>
  );
}

